<!--
  GgPushNotificationsCard.vue

  @version 1.0.0
  @description Self-contained UI card for managing Web Push subscription state.
               Drop it anywhere (profile page, settings dialog, drawer) — the
               component owns its lifecycle via usePushNotifications composable.

  @features
  - Status indicator (active / inactive) with explanatory text
  - Master toggle (subscribe / unsubscribe)
  - "Send test notification" button (visible only when subscribed)
  - iOS install hint when running in Mobile Safari (push needs PWA standalone)
  - Permission-denied hint with link to reset browser settings
  - Capability-not-supported state (graceful fallback)

  @usage
  <gg-push-notifications-card />
-->

<template>
  <gg-section-card
    title="Notifiche push"
    icon="notifications-active-outline"
    class="gg-push-card"
  >
    <!-- ── Capability not supported ─────────────────────────────────────── -->
    <div v-if="!isSupported" class="gg-push-card__notice">
      <v-icon size="20" class="gg-push-card__notice-icon">ms:info-outline</v-icon>
      <div>
        <div class="gg-push-card__notice-title">Non supportato su questo dispositivo</div>
        <div class="gg-push-card__notice-body">
          Il tuo browser non supporta le notifiche push. Prova con Chrome, Edge, Firefox
          o Safari aggiornati.
        </div>
      </div>
    </div>

    <!-- ── iOS standalone required ──────────────────────────────────────── -->
    <div
      v-else-if="iosNeedsInstall"
      class="gg-push-card__notice gg-push-card__notice--warn"
    >
      <v-icon size="20" class="gg-push-card__notice-icon">ms:install-mobile</v-icon>
      <div>
        <div class="gg-push-card__notice-title">Aggiungi l'app alla schermata Home</div>
        <div class="gg-push-card__notice-body">
          Su iOS le notifiche push funzionano solo quando l'app è installata.
          In Safari tocca <strong>Condividi</strong> &rarr; <strong>Aggiungi a Home</strong>,
          poi apri Gandolfi CMS dalla nuova icona e torna qui per attivarle.
        </div>
      </div>
    </div>

    <!-- ── Permission denied ────────────────────────────────────────────── -->
    <div
      v-else-if="permission === 'denied'"
      class="gg-push-card__notice gg-push-card__notice--error"
    >
      <v-icon size="20" class="gg-push-card__notice-icon">ms:notifications-off</v-icon>
      <div>
        <div class="gg-push-card__notice-title">Permesso negato</div>
        <div class="gg-push-card__notice-body">
          Hai negato le notifiche per questo sito. Per riabilitarle apri le impostazioni
          del browser per Gandolfi CMS e cambia <em>Notifiche</em> in <strong>Consenti</strong>,
          poi ricarica la pagina.
        </div>
      </div>
    </div>

    <!-- ── Main toggle row (always shown when push is reachable) ────────── -->
    <div v-if="canPushOnIOS" class="gg-push-card__row">
      <div class="gg-push-card__row-info">
        <div class="gg-push-card__row-title">
          {{ isSubscribed ? 'Notifiche attive' : 'Notifiche disattivate' }}
        </div>
        <div class="gg-push-card__row-subtitle">
          {{
            isSubscribed
              ? 'Riceverai un avviso anche quando l\'app è chiusa.'
              : 'Attiva per ricevere avvisi anche quando l\'app è chiusa.'
          }}
        </div>
      </div>
      <v-switch
        :model-value="isSubscribed"
        :loading="isLoading"
        :disabled="isLoading || permission === 'denied'"
        color="primary"
        hide-details
        density="compact"
        @update:model-value="onToggle"
      />
    </div>

    <!-- ── Test button (only when actively subscribed) ──────────────────── -->
    <div v-if="canPushOnIOS && isSubscribed" class="gg-push-card__actions">
      <v-btn
        variant="tonal"
        color="primary"
        size="small"
        :loading="isLoading"
        :disabled="isLoading"
        prepend-icon="ms:send"
        @click="onSendTest"
      >
        Invia notifica di test
      </v-btn>
    </div>
  </gg-section-card>
</template>

<script setup lang="ts">
import { usePushNotifications } from '@/composables/usePushNotifications'

defineOptions({ name: 'GgPushNotificationsCard' })

const {
  isSupported,
  permission,
  isSubscribed,
  isLoading,
  iosNeedsInstall,
  canPushOnIOS,
  subscribe,
  unsubscribe,
  sendTest,
} = usePushNotifications()

async function onToggle(value: boolean | null): Promise<void> {
  if (value) {
    await subscribe()
  } else {
    await unsubscribe()
  }
}

async function onSendTest(): Promise<void> {
  await sendTest()
}
</script>

<style lang="scss" scoped>
.gg-push-card {
  // Notice banners (info / warn / error)
  &__notice {
    display: flex;
    align-items: flex-start;
    gap: $spacing-sm;
    padding: $spacing-sm $spacing-md;
    border-radius: $border-radius;
    background-color: rgba(var(--v-theme-info), 0.08);
    color: rgba(var(--v-theme-on-surface), 0.85);
    font-size: $font-size-sm;
    line-height: 1.45;
    margin-bottom: $spacing-sm;

    &--warn {
      background-color: rgba(var(--v-theme-warning), 0.1);
    }

    &--error {
      background-color: rgba(var(--v-theme-error), 0.08);
    }
  }

  &__notice-icon {
    flex-shrink: 0;
    margin-top: 2px;
    color: rgba(var(--v-theme-on-surface), 0.7);
  }

  &__notice-title {
    font-weight: $font-weight-semibold;
    margin-bottom: 2px;
  }

  &__notice-body {
    color: rgba(var(--v-theme-on-surface), 0.7);
  }

  // Toggle row
  &__row {
    display: flex;
    align-items: center;
    gap: $spacing-md;
    padding: $spacing-xs 0;
  }

  &__row-info {
    flex: 1;
    min-width: 0;
  }

  &__row-title {
    font-size: $font-size-md;
    font-weight: $font-weight-semibold;
    color: rgb(var(--v-theme-on-surface));
  }

  &__row-subtitle {
    font-size: $font-size-sm;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-top: 2px;
  }

  // Actions row
  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: $spacing-md;
    padding-top: $spacing-sm;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  }
}
</style>
